import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';
import { 
  Users, ArrowLeft, Lock, Globe, Crown, Send, Heart,
  MessageSquare, MoreHorizontal, Trash2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CircleDetailPage = () => {
  const { circleId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [circle, setCircle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newPost, setNewPost] = useState('');
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    fetchCircle();
  }, [circleId]);

  const fetchCircle = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/circles/${circleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCircle(response.data);
    } catch (error) {
      console.error('Failed to fetch circle:', error);
      if (error.response?.status === 403) {
        toast.error('This is a private circle');
        navigate('/circles');
      } else if (error.response?.status === 404) {
        toast.error('Circle not found');
        navigate('/circles');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePost = async () => {
    if (!newPost.trim()) {
      toast.error('Please enter some content');
      return;
    }

    setPosting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/circles/${circleId}/posts?content=${encodeURIComponent(newPost)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Add new post to the list
      setCircle(prev => ({
        ...prev,
        recent_posts: [response.data.post, ...(prev.recent_posts || [])]
      }));
      setNewPost('');
      toast.success('Post created!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create post');
    } finally {
      setPosting(false);
    }
  };

  const handleJoin = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/circles/${circleId}/join`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Joined circle!');
      fetchCircle();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join');
    }
  };

  const handleLeave = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/circles/${circleId}/leave`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Left circle');
      navigate('/circles');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to leave');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this circle? This cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/circles/${circleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Circle deleted');
      navigate('/circles');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 lg:p-8">
          <div className="text-center py-12 text-gray-500">Loading circle...</div>
        </div>
      </DashboardLayout>
    );
  }

  if (!circle) {
    return (
      <DashboardLayout>
        <div className="p-6 lg:p-8">
          <div className="text-center py-12 text-gray-500">Circle not found</div>
        </div>
      </DashboardLayout>
    );
  }

  const isCreator = circle.created_by === user?.id;
  const isMember = circle.is_member;

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="circle-detail-page">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate('/circles')}
          className="mb-4 text-gray-600 hover:text-forest"
        >
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Circles
        </Button>

        {/* Circle Header */}
        <Card className="card-base mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="font-heading text-2xl font-bold text-forest">{circle.name}</h1>
                  {circle.is_private ? (
                    <Badge variant="outline"><Lock className="h-3 w-3 mr-1" /> Private</Badge>
                  ) : (
                    <Badge variant="outline"><Globe className="h-3 w-3 mr-1" /> Public</Badge>
                  )}
                </div>
                <p className="text-gray-600 mb-4">{circle.description}</p>
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <Users className="h-4 w-4" />
                    <span>{circle.member_count} members</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Crown className="h-4 w-4 text-terracotta" />
                    <span>Created by {circle.creator_name}</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-2">
                {isMember ? (
                  <>
                    {isCreator ? (
                      <Button 
                        variant="outline" 
                        onClick={handleDelete}
                        className="text-red-500 border-red-200 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4 mr-2" /> Delete Circle
                      </Button>
                    ) : (
                      <Button variant="outline" onClick={handleLeave}>
                        Leave Circle
                      </Button>
                    )}
                  </>
                ) : (
                  <Button 
                    onClick={handleJoin}
                    className="bg-terracotta hover:bg-terracotta/90 text-white"
                  >
                    Join Circle
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Post Creation (Members Only) */}
        {isMember && (
          <Card className="card-base mb-6">
            <CardContent className="p-4">
              <div className="flex gap-3">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={user?.avatar} />
                  <AvatarFallback className="bg-terracotta text-white">
                    {user?.full_name?.charAt(0) || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <Textarea
                    value={newPost}
                    onChange={(e) => setNewPost(e.target.value)}
                    placeholder="Share something with the circle..."
                    className="input-base min-h-[80px] mb-2"
                  />
                  <div className="flex justify-end">
                    <Button 
                      onClick={handleCreatePost}
                      disabled={posting || !newPost.trim()}
                      className="bg-forest hover:bg-forest/90 text-white"
                    >
                      {posting ? 'Posting...' : (
                        <>
                          <Send className="h-4 w-4 mr-2" /> Post
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Posts Feed */}
        <div className="space-y-4">
          <h2 className="font-heading text-lg font-semibold text-forest">Circle Posts</h2>
          
          {circle.recent_posts?.length > 0 ? (
            circle.recent_posts.map(post => (
              <Card key={post.id} className="card-base">
                <CardContent className="p-4">
                  <div className="flex gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={post.author_avatar} />
                      <AvatarFallback className="bg-forest text-white">
                        {post.author_name?.charAt(0) || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-forest">{post.author_name}</span>
                        <span className="text-xs text-gray-400">
                          {new Date(post.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-gray-700 whitespace-pre-wrap">{post.content}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card className="card-base">
              <CardContent className="py-12 text-center">
                <MessageSquare className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">
                  {isMember ? 'No posts yet. Be the first to share!' : 'Join to see and create posts'}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CircleDetailPage;
